import { formatDateTimeUrlDate } from 'common/UrlDate';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { useRouteMatch } from 'react-router';
import { useDishFistCartPositionContext } from 'src/pages/consumer/components/ConsumerHeaderWrapper';
import { PromoContent } from 'src/pages/consumer/components/DishFirstHeader/PromoContent';
import { ChevronLeft, Close } from 'src/shared/design-system/Icon';
import { ConsumerRoutes } from 'src/shared/Routes';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { ScreenWidth, ScreenWidthValues } from 'web-common/src/shared/styles';
import { DeliveryDateMismatchWarning } from '../DeliveryDateMismatchWarning';
import {
  BackButtonContainer,
  CalendarIcon,
  CloseContainer,
  DeliveryDate,
  DeliveryDateContainer,
  EditDeliveryDateButton,
  HeaderContainer,
  TopContainer,
  YourCart,
  YourCartTextContainer,
} from './MultiCartHeaderStyles';
import { PeopleAlsoOrdered } from './MultiCartPeopleAlsoOrdered';
import { MultiCartTotals } from './MultiCartTotals';

const MAX_SCROLL_HEIGHT = 36;

export const useMultiCartScrollHeader = () => {
  const [headerPercentScrolled, setHeaderPercentScrolled] = useState(0);

  const handleScroll = (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const { scrollTop } = event.currentTarget;
    const percentScrolled = Math.round((scrollTop / MAX_SCROLL_HEIGHT) * 100);
    setHeaderPercentScrolled(Math.min(percentScrolled, 100));
  };

  return {
    handleScroll,
    headerPercentScrolled,
  };
};

const DeliveryDateComponent = ({
  floatForward,
  isEditingOrder,
  deliveryDate,
  onEditDeliveryDate,
  isMediumOrLargerScreen,
}) => (
  <DeliveryDateContainer floatForward={floatForward}>
    {!isEditingOrder && deliveryDate && (
      <EditDeliveryDateButton onClick={onEditDeliveryDate}>
        <DeliveryDate>
          Delivery on{' '}
          <span className='underline'>
            {deliveryDate?.weekdayShort}, {deliveryDate?.monthShort} {deliveryDate?.day}
          </span>
        </DeliveryDate>
        <CalendarIcon />
      </EditDeliveryDateButton>
    )}
    {isMediumOrLargerScreen && (
      <div className='mt-2'>
        <DeliveryDateMismatchWarning rightAlignIcon />
      </div>
    )}
  </DeliveryDateContainer>
);

interface MultiCartHeaderProps {
  deliveryDate: DateTime | undefined;
  onEditDeliveryDate: () => void;
  headerPercentScrolled: number;
  isEditingOrder: boolean;
  isEmptyCart: boolean;
  onCheckoutClick: () => void;
}

export const MultiCartHeader: React.FC<MultiCartHeaderProps> = ({
  deliveryDate,
  onEditDeliveryDate,
  headerPercentScrolled,
  isEditingOrder,
  isEmptyCart,
  onCheckoutClick,
}) => {
  const deliveryDateUrl = deliveryDate ? formatDateTimeUrlDate(deliveryDate) : undefined;

  const cartPositionState = useDishFistCartPositionContext();
  const { windowDimensions } = useScreenContext();
  const isMediumOrLargerScreen = windowDimensions.width > ScreenWidthValues[ScreenWidth.SMALL];
  const isMediumScreen =
    windowDimensions.width > ScreenWidthValues[ScreenWidth.SMALL] &&
    windowDimensions.width < ScreenWidthValues[ScreenWidth.LARGE];

  const match = useRouteMatch(ConsumerRoutes.CONSUMER_ORDER_ONE_TIME);
  const isOnShefMenu = !!match;
  const showCloseButton = (!isOnShefMenu && isMediumOrLargerScreen) || (isOnShefMenu && isMediumScreen);

  const deliveryDateComponentProps = {
    floatForward: headerPercentScrolled === 100,
    deliveryDate,
    onEditDeliveryDate,
    isEditingOrder,
    isMediumOrLargerScreen,
  };

  return (
    <HeaderContainer>
      {isMediumOrLargerScreen && <PeopleAlsoOrdered deliveryDate={deliveryDateUrl} isEditingOrder={isEditingOrder} />}
      <TopContainer hasItems={!isEmptyCart}>
        <YourCartTextContainer>
          {/* only shows on desktop */}
          {showCloseButton && (
            <CloseContainer onClick={cartPositionState.close}>
              <Close width={12} height={12} />
            </CloseContainer>
          )}
          {/* only shows on mobile */}
          <BackButtonContainer>
            <ChevronLeft width={12} height={12} />
          </BackButtonContainer>
          <YourCart>Your cart</YourCart>
        </YourCartTextContainer>

        {!isEmptyCart && !isMediumOrLargerScreen ? <DeliveryDateComponent {...deliveryDateComponentProps} /> : <div />}
      </TopContainer>
      {!isMediumOrLargerScreen && (
        <>
          <PeopleAlsoOrdered deliveryDate={deliveryDateUrl} isEditingOrder={isEditingOrder} />
          <PromoContent showInline />
        </>
      )}
      {!isEmptyCart && isMediumOrLargerScreen && <DeliveryDateComponent {...deliveryDateComponentProps} />}
      {!isEmptyCart && (
        <MultiCartTotals
          isEditingOrder={isEditingOrder}
          floatForward={headerPercentScrolled === 100}
          onCheckoutClick={onCheckoutClick}
        />
      )}
    </HeaderContainer>
  );
};
